import React, { useState, useEffect } from 'react';
import { dispatchEvent } from '../../shared/events';
import { getLimitsForTier } from '../../shared/plans';

const VARIANT_OVERRIDE_KEY = 'signupModalVariantOverride';
const VARIANT_OPTIONS = [
  { value: null, label: 'Auto (flag)' },
  { value: 'current', label: 'Current' },
];

function readVariantOverride() {
  if (typeof localStorage === 'undefined') return null;
  const value = localStorage.getItem(VARIANT_OVERRIDE_KEY);
  return value === 'current' ? value : null;
}

function writeVariantOverride(value) {
  if (typeof localStorage === 'undefined') return;
  if (value) localStorage.setItem(VARIANT_OVERRIDE_KEY, value);
  else localStorage.removeItem(VARIANT_OVERRIDE_KEY);
  window.dispatchEvent(new Event('signupModalVariantOverrideChanged'));
}

const silverLists = getLimitsForTier('silver').ranking_lists;
const goldLists = getLimitsForTier('gold').ranking_lists;

const funnels = [
  { label: 'Subscribe', event: 'requestedSubscribe', requires: 'any' },
  { label: 'Expired (valid card)', event: 'requestBlockedByExpiredSubscription', requires: 'subscribed' },
  { label: 'Out of lists → Silver', event: 'triedToExceedRankingLists', detail: { requested: silverLists }, requires: 'subscribed' },
  { label: 'Out of lists → Gold', event: 'triedToExceedRankingLists', detail: { requested: goldLists }, requires: 'subscribed' },
  { label: 'Out of lists → Contact', event: 'triedToExceedRankingLists', detail: { requested: goldLists + 1 }, requires: 'subscribed' },
  { label: 'Curious → Silver', event: 'browsingRankingLists', detail: { requested: silverLists }, requires: 'subscribed' },
  { label: 'Curious → Gold', event: 'browsingRankingLists', detail: { requested: goldLists }, requires: 'subscribed' },
  { label: 'Curious → Contact', event: 'browsingRankingLists', detail: { requested: goldLists + 1 }, requires: 'subscribed' },
  { label: 'Redeem coupon', event: 'showRedeemCoupon', requires: 'free' },
  { label: 'Requests exhausted', event: 'requestsExhausted', requires: 'free' },
  { label: 'Out of daily searches', event: 'triedToExceedDailySearches', requires: 'subscribed' },
];

const panelStyle = {
  position: 'fixed',
  bottom: '10px',
  left: '10px',
  zIndex: 99999,
  background: '#1a1a2e',
  color: '#ccc',
  borderRadius: '8px',
  fontSize: '12px',
  fontFamily: 'monospace',
  boxShadow: '0 2px 12px rgba(0,0,0,0.3)',
};

const toggleStyle = {
  display: 'block',
  color: '#7eb8ff',
  cursor: 'pointer',
  padding: '8px 12px',
  background: 'none',
  border: 'none',
  fontSize: '11px',
  fontFamily: 'monospace',
  textTransform: 'uppercase',
  letterSpacing: '1px',
};

const linkStyle = {
  display: 'block',
  cursor: 'pointer',
  padding: '3px 16px',
  textDecoration: 'none',
  border: 'none',
  background: 'none',
  fontSize: '12px',
  fontFamily: 'monospace',
  textAlign: 'left',
  width: '100%',
};

function FunnelLink({ funnel, enabled }) {
  const [hovered, setHovered] = useState(false);
  const color = !enabled ? '#555' : hovered ? '#fff' : '#7eb8ff';
  return (
    <button
      style={{ ...linkStyle, color, cursor: enabled ? 'pointer' : 'default' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={enabled ? () => dispatchEvent(funnel.event, funnel.detail || {}) : undefined}
      disabled={!enabled}
    >{funnel.label}</button>
  );
}

const variantHeadingStyle = {
  ...linkStyle,
  color: '#888',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  fontSize: '10px',
  paddingTop: '8px',
  cursor: 'default',
};

function VariantToggle({ variant, current, onSelect }) {
  const [hovered, setHovered] = useState(false);
  const isActive = variant.value === current;
  const color = isActive ? '#67cf75' : hovered ? '#fff' : '#7eb8ff';
  return (
    <button
      style={{ ...linkStyle, color }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(variant.value)}
    >{isActive ? '● ' : '  '}{variant.label}</button>
  );
}

export default function DebugFunnels({ user }) {
  const [open, setOpen] = useState(false);
  const [variantOverride, setVariantOverride] = useState(readVariantOverride());
  const isSubscribed = !user.isFreeUser && user.subscription;

  useEffect(() => {
    const sync = () => setVariantOverride(readVariantOverride());
    window.addEventListener('signupModalVariantOverrideChanged', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('signupModalVariantOverrideChanged', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const isEnabled = (funnel) => {
    if (funnel.requires === 'any') return true;
    if (funnel.requires === 'free') return user.isFreeUser;
    if (funnel.requires === 'subscribed') return isSubscribed;
    return true;
  };

  const handleSelectVariant = value => {
    writeVariantOverride(value);
    setVariantOverride(value);
  };

  return (
    <div style={panelStyle}>
      <button style={toggleStyle} onClick={() => setOpen(!open)}>
        {open ? '▼ Funnels' : '▶ Funnels'}
      </button>
      {open && (
        <div style={{padding: '0 0 8px'}}>
          {funnels.map((f, i) => (
            <FunnelLink key={i} funnel={f} enabled={isEnabled(f)} />
          ))}
          <span style={variantHeadingStyle}>Signup modal variant</span>
          {VARIANT_OPTIONS.map(opt => (
            <VariantToggle
              key={String(opt.value)}
              variant={opt}
              current={variantOverride}
              onSelect={handleSelectVariant}
            />
          ))}
        </div>
      )}
    </div>
  );
}
